import { throwsNetworkError } from "@/utils/http/NetworkManagerMixin";
import { EServices } from "@/types";
import JackocoinsNetworkManagerMixin from "@/mixins/JackocoinsNetworkMangerMixin";

import { Mixins, Component, Ref, Watch, Inject } from "vue-property-decorator";
import { namespace } from "vuex-class";
import BankAccountEditor, {
  IBankAccountEditor,
} from "../../components/bank-account-editor/BankAccountEditor";
import { IBank, IBankAccount, IBanksClient } from "../../types";
import VEmptyState from "@/vuetify-extensions/VEmptyState.vue";
import NetworkErrorImage from "@/components/NetworkErrorImage.vue";

const BankModule = namespace("BankModule");

interface IBankAccountRenderable extends IBankAccount {
  deleting?: boolean;
  restoring?: boolean;
}

@Component({
  components: {
    BankAccountEditor,
    VEmptyState,
    NetworkErrorImage,
  },
})
export default class Banks extends Mixins(JackocoinsNetworkManagerMixin) {
  type: "available" | "deleted" = "available";
  types = ["available", "deleted"];
  initialLoad = false;

  @Ref()
  bankAccountEditor!: IBankAccountEditor;

  @BankModule.State
  bankAccounts!: IBankAccount[];

  @BankModule.State
  newBankAccounts!: IBankAccount[];

  @BankModule.State
  banks!: IBank[];

  @BankModule.State
  deletedBanks!: IBankAccount[];

  @Inject(EServices.banks)
  banksClient!: IBanksClient;

  get displayedBankAccounts(): IBankAccountRenderable[] {
    return this.type == "available"
      ? this.bankAccounts.map((el) => ({
          ...el,
          makingDefault: false,
          deleting: false,
        }))
      : this.deletedBanks.map((el) => ({
          ...el,
          makingDefault: false,
          deleting: false,
          restoring: false,
        }));
  }

  get displayedNewBankAccounts(): IBankAccountRenderable[] {
    return this.type == "available"
      ? this.newBankAccounts.map((el) => ({
          ...el,
          makingDefault: false,
          deleting: false,
        }))
      : this.deletedBanks.map((el) => ({
          ...el,
          makingDefault: false,
          deleting: false,
          restoring: false,
        }));
  }

  getBank(bankAccount: IBankAccount): IBank {
    return this.banks.find((bank: IBank) => bank.id == bankAccount.bank.id)!;
  }

  async makeDefault(bankAccount: IBankAccountRenderable) {
    const response = await authenticate({
      message: `Your are about to change your default bank account details`,
      authenticator: async (password: string): Promise<any> => {
        const resp = await this.banksClient.makeBankDefault(
          bankAccount.id,
          password
        );
        if (resp) await this.getNewBankDetails();
        return true;
      },
    });
    if (!response) return;
    toast({
      message: "Default bank account changed",
      icon: "mdi-check",
      iconColor: "green",
    });
  }

  @throwsNetworkError()
  async deleteBankAccount(bankAccount: IBankAccountRenderable) {
    bankAccount.deleting = true;
    await this.banksClient.deleteBank(bankAccount.id);
    bankAccount.deleting = false;
    this.getNewBankDetails();
    toast({
      message: "Bank account deleted",
      icon: "mdi-check",
      iconColor: "green",
    });
  }

  @throwsNetworkError()
  async discardBankAccount(bankAccount: IBankAccountRenderable) {
    const result = await confirm({
      icon: "mdi-delete",
      title: "Discard Bank Account",
      message: "This process cannot be reverted",
    });
    if (!result) return;
    bankAccount.deleting = true;
    await this.banksClient.discardBank(bankAccount.id);
    bankAccount.deleting = false;
    this.getNewBankDetails();
    toast({
      message: "Bank account deleted",
      icon: "mdi-check",
      iconColor: "green",
    });
  }

  @throwsNetworkError()
  async restoreBankAccount(bankAccount: IBankAccountRenderable) {
    bankAccount.restoring = true;
    await this.banksClient.restoreBank(bankAccount.id);
    bankAccount.restoring = false;
    this.type = "available";
    toast({
      message: "Bank account restored",
      icon: "mdi-check",
      iconColor: "green",
    });
  }

  @throwsNetworkError()
  async getBankDetails() {
    await this.banksClient.getBankAccounts();
  }

  @throwsNetworkError()
  async getNewBankDetails() {
    await this.banksClient.getNewBankAccounts();
  }

  @Watch("banks")
  @Watch("deletedBanks")
  @Watch("bankAccounts")
  @Watch("newBankAccounts")
  onBanks() {
    this.initialLoad = true;
  }

  @Watch("error.deleteBankAccount")
  @Watch("error.discardBankAccount")
  @Watch("error.restoreBankAccount")
  onError(message: string) {
    if (message)
      toast({ icon: "mdi-exclamation-thick", iconColor: "red", message });
  }

  mounted() {
    this.getNewBankDetails();
    this.getBankDetails();
  }
}
